import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, Plus, Minus, ShoppingCart, Check, ArrowLeft } from 'lucide-react';
import { getProductBySlug, products } from '../data/products';
import { useCart } from '../context/CartContext';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    addItem
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'desc' | 'reviews'>('desc');
  const product = slug ? getProductBySlug(slug) : undefined;
  useEffect(() => {
    window.scrollTo(0, 0);
    setQuantity(1);
  }, [slug]);
  if (!product) {
    return <div className="min-h-screen pt-32 pb-24 flex flex-col items-center justify-center bg-brand-cream" data-id="element-340">
        <h1 className="text-4xl font-serif font-bold text-brand-dark mb-4" data-id="element-341">
          Product niet gevonden
        </h1>
        <button onClick={() => navigate('/shop')} className="text-brand-red hover:underline flex items-center gap-2" data-id="element-342">
          <ArrowLeft size={16} data-id="element-343" /> Terug naar shop
        </button>
      </div>;
  }
  const relatedProducts = products.filter(p => p.id !== product.id).slice(0, 3);
  return <main className="pt-32 pb-24 bg-white min-h-screen" data-id="element-344">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-345">
        {/* Breadcrumbs */}
        <nav className="text-sm text-brand-gray mb-8 flex items-center gap-2" data-id="element-346">
          <Link to="/" className="hover:text-brand-red transition-colors" data-id="element-347">
            Home
          </Link>
          <span data-id="element-348">/</span>
          <Link to="/shop" className="hover:text-brand-red transition-colors" data-id="element-349">
            Kruiden
          </Link>
          <span data-id="element-350">/</span>
          <span className="text-brand-dark font-medium" data-id="element-351">{product.name}</span>
        </nav>

        {/* Product Top */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 mb-24" data-id="element-352">
          {/* Image */}
          <div className="w-full lg:w-1/2" data-id="element-353">
            <div className="bg-brand-cream rounded-3xl overflow-hidden aspect-[4/5] relative" data-id="element-354">
              {product.tag && <span className="absolute top-6 left-6 z-10 bg-brand-dark text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wide" data-id="element-355">
                  {product.tag}
                </span>}
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-356" />
            </div>
          </div>

          {/* Info */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center" data-id="element-357">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-brand-dark mb-4" data-id="element-358">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mb-6" data-id="element-359">
              <div className="flex items-center gap-1" data-id="element-360">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} className={i < Math.floor(product.rating) ? 'fill-brand-yellow text-brand-yellow' : 'text-gray-300'} data-id="element-361" />)}
              </div>
              <span className="text-brand-gray text-sm" data-id="element-362">
                ({product.reviews} klantbeoordelingen)
              </span>
            </div>

            <p className="text-3xl font-bold text-brand-dark mb-8" data-id="element-363">
              €{product.price.toFixed(2)}
            </p>

            <p className="text-brand-gray text-lg mb-8 leading-relaxed" data-id="element-364">
              {product.shortDescription}
            </p>

            {/* Add to Cart Form */}
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-12 pb-12 border-b border-gray-100" data-id="element-365">
              <div className="flex items-center border-2 border-gray-200 rounded-full h-14 w-full sm:w-32" data-id="element-366">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-full flex items-center justify-center text-brand-gray hover:text-brand-dark transition-colors" data-id="element-367">
                  <Minus size={16} data-id="element-368" />
                </button>
                <span className="flex-1 text-center font-bold text-brand-dark" data-id="element-369">
                  {quantity}
                </span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-full flex items-center justify-center text-brand-gray hover:text-brand-dark transition-colors" data-id="element-370">
                  <Plus size={16} data-id="element-371" />
                </button>
              </div>

              <button onClick={() => {
              for (let i = 0; i < quantity; i++) addItem(product);
            }} className="w-full sm:flex-1 h-14 bg-brand-red text-white rounded-full font-medium hover:bg-brand-darkRed hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-372">
                <ShoppingCart size={20} data-id="element-373" />
                Toevoegen aan winkelwagen
              </button>
            </div>

            <div className="text-sm text-brand-gray" data-id="element-374">
              <p className="mb-2" data-id="element-375">
                <span className="font-bold text-brand-dark" data-id="element-376">
                  Artikelnummer:
                </span>{' '}
                {product.slug}
              </p>
              <p data-id="element-377">
                <span className="font-bold text-brand-dark" data-id="element-378">Categorie:</span>{' '}
                {product.category}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-24" data-id="element-379">
          <div className="flex gap-8 border-b border-gray-200 mb-8" data-id="element-380">
            <button onClick={() => setActiveTab('desc')} className={`pb-4 text-lg font-serif font-bold transition-colors relative ${activeTab === 'desc' ? 'text-brand-dark' : 'text-brand-gray hover:text-brand-dark'}`} data-id="element-381">
              Beschrijving
              {activeTab === 'desc' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-red" data-id="element-382"></span>}
            </button>
            <button onClick={() => setActiveTab('reviews')} className={`pb-4 text-lg font-serif font-bold transition-colors relative ${activeTab === 'reviews' ? 'text-brand-dark' : 'text-brand-gray hover:text-brand-dark'}`} data-id="element-383">
              Beoordelingen ({product.reviews})
              {activeTab === 'reviews' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-red" data-id="element-384"></span>}
            </button>
          </div>

          <div className="max-w-3xl" data-id="element-385">
            {activeTab === 'desc' ? <div className="prose prose-lg text-brand-gray" data-id="element-386">
                <p className="mb-8 leading-relaxed" data-id="element-387">{product.description}</p>
                <h4 className="font-serif font-bold text-brand-dark text-xl mb-4" data-id="element-388">
                  Voordelen op een rij:
                </h4>
                <ul className="space-y-3" data-id="element-389">
                  {product.benefits.map((benefit, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-390">
                      <Check className="w-6 h-6 text-brand-red flex-shrink-0" data-id="element-391" />
                      <span data-id="element-392">{benefit}</span>
                    </li>)}
                </ul>
              </div> : <div className="text-brand-gray" data-id="element-393">
                <p data-id="element-394">
                  Er zijn nog geen geschreven beoordelingen voor dit product,
                  maar het heeft wel {product.reviews} sterrenbeoordelingen
                  ontvangen.
                </p>
              </div>}
          </div>
        </div>

        {/* Related Products */}
        <div data-id="element-395">
          <h2 className="text-3xl font-serif font-bold text-brand-dark mb-10" data-id="element-396">
            Gerelateerde producten
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-397">
            {relatedProducts.map(related => <div key={related.id} className="group relative bg-brand-cream rounded-2xl p-4 transition-all duration-300 flex flex-col" data-id="element-398">
                <Link to={`/product/${related.slug}`} className="block relative aspect-square overflow-hidden rounded-xl mb-6 bg-white" data-id="element-399">
                  <img src={related.image} alt={related.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-400" />
                </Link>
                <div className="flex-grow flex flex-col" data-id="element-401">
                  <Link to={`/product/${related.slug}`} className="hover:text-brand-red transition-colors" data-id="element-402">
                    <h3 className="font-serif font-semibold text-lg text-brand-dark mb-2 leading-tight" data-id="element-403">
                      {related.name}
                    </h3>
                  </Link>
                  <div className="mt-auto flex items-center justify-between pt-4" data-id="element-404">
                    <span className="text-xl font-bold text-brand-dark" data-id="element-405">
                      €{related.price.toFixed(2)}
                    </span>
                    <button onClick={() => addItem(related)} className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-brand-dark hover:bg-brand-red hover:text-white transition-colors" data-id="element-406">
                      <Plus size={20} data-id="element-407" />
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </main>;
}